import React from "react";
import {
  View,
  PanResponderInstance,
  PanResponder,
  GestureResponderEvent,
  PanResponderGestureState,
  StyleSheet,
  ActivityIndicator,
  Dimensions,
  EmitterSubscription,
  Platform,
} from "react-native";
import { connect } from "react-redux";

import AnimatedList from "./AnimatedList.component";
import { HomeThot } from "../../models/HomeThot";
import { ThotType } from "../../models/Thot.type";
import { apiGetUserThots } from "../../api/users.api";

type Props = {
  userId?: string;
  onlyRoots?: boolean;
};

type State = {
  list: HomeThot[];
  loadingVisible: boolean;
  reloadHintVisible: boolean;
  viewportVersion: number;
};

const THOT_WIDTH = 134;
const HEADER_HEIGHT = 56;
const FOOTER_HEIGHT = 48;
const TITLE_LINE_HEIGHT = 14;
const TITLE_PADDING_Y = 7;
const COMPACT_TITLE_HEIGHT = TITLE_LINE_HEIGHT + TITLE_PADDING_Y * 2;
const THOT_SEPERATOR = 20;
const SIDE_PADDING_LEFT = 20;
const SIDE_PADDING_RIGHT = 40;
const TOUCH_THOT_WIDTH = THOT_WIDTH + THOT_SEPERATOR;
const TOUCH_SENSITIVITY = 5;
const LIST_MOVE_DELAY = 0.1;
const MOVE_TO_DECAY = 250;
const STOP_MOVE_DECAY = 0.1;
const THOT_MOVE_DECAY = 0.93;
const RELOAD_TRESHOLD = 40;
const SCROLL_OUT_BREAKS = 0.4;

const mPow = Math.pow;
const mFloor = Math.floor;

const getThotHeight = () => {
  const screen = Dimensions.get("window");
  return (
    COMPACT_TITLE_HEIGHT +
    Math.round(THOT_WIDTH * ((screen.height - HEADER_HEIGHT) / Math.max(screen.width, 1))) +
    FOOTER_HEIGHT
  );
};

const getScreenWidth = () => Dimensions.get("window").width;
const getListWidth = (count: number) => {
  if (count <= 0) return 0;
  return (count - 1) * TOUCH_THOT_WIDTH + THOT_WIDTH;
};

class MyStartedThoughtsList extends React.Component<Props, State> {
  state: State = {
    list: [],
    loadingVisible: false,
    reloadHintVisible: false,
    viewportVersion: 0,
  };

  _animatedList: AnimatedList;
  _panResponder: PanResponderInstance;
  _dimensionsSubscription?: EmitterSubscription;

  _listWidth = 0;
  _viewWidth = 0;
  _touchCorrection = 0;

  _decaying = false;
  _moving = false;
  _animId: number;
  _loading = false;
  _reloading = false;
  _reloadIntervalId = null;

  _listX = 0;
  _offsetX = 0;
  _moveToOffsetX = 0;
  _thotX = 0;
  _touchedIndex = 0;
  _lastIndex = 0;

  constructor(props) {
    super(props);

    this._panResponder = PanResponder.create({
      onMoveShouldSetPanResponder: this._onMoveShouldSetPan,
      onPanResponderGrant: this._onPanGrant,
      onPanResponderMove: this._onPanMove,
      onPanResponderRelease: this._onPanRelease,
      onPanResponderTerminate: this._onPanRelease,
    });
  }

  componentDidMount() {
    this._dimensionsSubscription = Dimensions.addEventListener("change", this._onDimensionsChange);
    this._syncListMetrics(this.state.list.length);
    this._loadStartedThots();
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    if (this.props.userId && this.props.userId !== prevProps.userId) {
      this._loadStartedThots();
    }

    if (prevState.list.length !== this.state.list.length) {
      this._syncListMetrics(this.state.list.length);

      const min = this._getMinOffset();

      if (this._listX < min) {
        this._listX = min;
        this._offsetX = min;
        this._moveToOffsetX = min;
        this._animatedList?.moveList(this._listX, 0, this._touchedIndex);
      }
    }
  }

  componentWillUnmount() {
    this._dimensionsSubscription?.remove();
    if (this._reloadIntervalId) clearInterval(this._reloadIntervalId);
    this._stopMoveAnimation();
    this._stopDecayAnimation();
  }

  _onDimensionsChange = () => {
    this.setState(({ viewportVersion }) => ({ viewportVersion: viewportVersion + 1 }));
  };

  _syncListMetrics = (length: number) => {
    this._lastIndex = length - 1;
    if (this._lastIndex < 0) this._lastIndex = 0;
    this._listWidth = this._lastIndex * TOUCH_THOT_WIDTH + THOT_WIDTH;
  };

  _getMinOffset = () => {
    let min = this._listWidth - this._viewWidth;
    if (min < 0) min = 0;
    else min = -min;
    return min;
  };

  _isStartedThot = (thot: ThotType): boolean => {
    if (!thot) return false;
    if (!thot.parent) return true;
    if (!thot.root) return false;
    return typeof thot.root === "string"
      ? thot.root === thot._id
      : thot.root._id === thot._id;
  };

  _toHomeThot = (thot: ThotType): HomeThot => ({
    current: thot,
    root: thot,
    user:
      typeof thot.user === "string" ? ({ _id: thot.user, name: "" } as any) : thot.user,
  });

  _loadStartedThots = () => {
    if (this._loading) return;

    this._loading = true;
    if (!this.state.loadingVisible) this.setState({ loadingVisible: true });

    apiGetUserThots()
      .then((thots: ThotType[]) => {
        const list = thots
          .filter((thot) => (this.props.onlyRoots === false ? true : this._isStartedThot(thot)))
          .sort((a, b) => +new Date(b.created_at) - +new Date(a.created_at))
          .map(this._toHomeThot);

        this._loading = false;
        this.setState({ list, loadingVisible: false });

        if (this._reloading) {
          this._reloading = false;
          this._decayTo(0);
        }
      })
      .catch(() => {
        this._loading = false;
        this.setState({ list: [], loadingVisible: false });
      });
  };

  _onMoveShouldSetPan = (
    evt: GestureResponderEvent,
    gestureState: PanResponderGestureState
  ) => {
    if (this._reloading) return false;
    const { dx, dy } = gestureState;
    const absDx = Math.abs(dx);
    const absDy = Math.abs(dy);
    if (absDy > TOUCH_SENSITIVITY && absDy >= absDx) return false;
    return absDx > TOUCH_SENSITIVITY && absDx > absDy * 1.5;
  };

  _onPanGrant = (
    evt: GestureResponderEvent,
    gestureState: PanResponderGestureState
  ) => {
    if (this.state.reloadHintVisible) this.setState({ reloadHintVisible: false });
    this._animatedList?.moveList(this._listX, 0, this._touchedIndex);
    let i = mFloor(
      (gestureState.x0 - this._listX - this._touchCorrection) / TOUCH_THOT_WIDTH
    );
    if (i < 0) i = 0;
    if (i > this._lastIndex) i = this._lastIndex;
    this._touchedIndex = i;

    this._offsetX = this._listX;
    this._moveToOffsetX = this._listX;
    this._startMoveAnimation();
  };

  _onPanMove = (
    evt: GestureResponderEvent,
    gestureState: PanResponderGestureState
  ) => {
    this._moveToOffsetX = this._offsetX + gestureState.dx;
    if (this._moveToOffsetX > 0) {
      this._moveToOffsetX *= SCROLL_OUT_BREAKS;
    }

    const reloadHintVisible = this._offsetX >= 0 && gestureState.dx > 0;
    if (reloadHintVisible !== this.state.reloadHintVisible) {
      this.setState({ reloadHintVisible });
    }

    const min = this._getMinOffset();
    if (this._moveToOffsetX < min) {
      this._moveToOffsetX = min + (this._moveToOffsetX - min) * SCROLL_OUT_BREAKS;
    }
  };

  _onPanRelease = (
    evt: GestureResponderEvent,
    gestureState: PanResponderGestureState
  ) => {
    if (this.state.reloadHintVisible) this.setState({ reloadHintVisible: false });
    let des = this._offsetX + gestureState.dx;
    if (des > 0) des *= SCROLL_OUT_BREAKS;
    this._thotX = des - this._listX;

    if (des > RELOAD_TRESHOLD) {
      this._reloadStartedThots();
      return;
    }

    let end = mPow(gestureState.vx, 2) * MOVE_TO_DECAY;
    if (gestureState.vx < 0) end *= -1;
    end += gestureState.dx + this._offsetX;

    const min = this._getMinOffset();

    if (end > 0) end = 0;
    else if (end < min) end = min;

    this._decayTo(end);
  };

  _reloadStartedThots = () => {
    this._reloading = true;
    this._reloadIntervalId = setInterval(() => {
      if (!this._decaying) {
        clearInterval(this._reloadIntervalId);
        this._loadStartedThots();
      }
    }, 250);
    this._decayTo(RELOAD_TRESHOLD);
  };

  _startMoveAnimation = () => {
    this._stopDecayAnimation();
    this._moving = true;
    this._animId = requestAnimationFrame(this._moveAnimationFrame);
  };

  _stopMoveAnimation = () => {
    this._moving = false;
    if (this._animId) {
      cancelAnimationFrame(this._animId);
      this._animId = undefined;
    }
  };

  _moveAnimationFrame = () => {
    const moveX = this._moveToOffsetX - this._listX;
    this._moveList(moveX);

    if (this._moving) this._animId = requestAnimationFrame(this._moveAnimationFrame);
    else this._stopMoveAnimation();
  };

  _startDecayAnimation = () => {
    this._stopMoveAnimation();
    this._decaying = true;
    this._animId = requestAnimationFrame(this._decayAnimationFrame);
  };

  _stopDecayAnimation = () => {
    this._decaying = false;
    if (this._animId) {
      cancelAnimationFrame(this._animId);
      this._animId = undefined;
    }
  };

  _decayAnimationFrame = () => {
    const moveX = this._moveToOffsetX - this._listX;
    if (moveX < STOP_MOVE_DECAY && moveX > -STOP_MOVE_DECAY) {
      this._decaying = false;
    }

    this._thotX = this._thotX * THOT_MOVE_DECAY;
    this._moveList(moveX, true);

    if (this._decaying) this._animId = requestAnimationFrame(this._decayAnimationFrame);
    else this._stopDecayAnimation();
  };

  _decayTo = (end = 0) => {
    this._offsetX = end;
    this._moveToOffsetX = end;
    if (!this._decaying) this._startDecayAnimation();
  };

  _moveList = (moveX: number, decay: boolean = false) => {
    this._listX = this._listX + moveX * LIST_MOVE_DELAY;

    if (!decay) this._animatedList?.moveList(this._listX, moveX, this._touchedIndex);
    else this._animatedList?.moveList(this._listX, this._thotX, this._touchedIndex);
  };

  _onLayout = (event) => {
    const { width, x } = event.nativeEvent.layout;
    this._viewWidth = width - SIDE_PADDING_LEFT - SIDE_PADDING_RIGHT;
    this._touchCorrection = THOT_SEPERATOR / 2 - x;

    const min = this._getMinOffset();
    if (this._listX < min) {
      this._listX = min;
      this._offsetX = min;
      this._moveToOffsetX = min;
      this._animatedList?.moveList(this._listX, 0, this._touchedIndex);
    }
  };

  _setList = (ref: AnimatedList) => (this._animatedList = ref);

  render() {
    const { list, loadingVisible, reloadHintVisible } = this.state;
    const thotHeight = getThotHeight();
    const containerWidth = Math.min(getScreenWidth(), getListWidth(list.length) + 30);

    if (!list.length) return <View style={styles.emptyWrap} />;

    return (
      <View
        style={[styles.container, { width: containerWidth, height: thotHeight }]}
        onLayout={this._onLayout}
        {...this._panResponder.panHandlers}
      >
        <View style={[styles.reload, { height: thotHeight }]}>
          <View
            style={[
              styles.reloadIndicator,
              (loadingVisible || reloadHintVisible) && styles.reloadIndicatorVisible,
            ]}
          >
            <ActivityIndicator color="#000" />
          </View>
        </View>
        <AnimatedList
          thots={list}
          contentOffset={this._listX}
          onRef={this._setList}
          style={styles.list}
          thotStyle={[styles.thot, { height: thotHeight }]}
        />
      </View>
    );
  }
}

const mapStateToProps = ({ user }) => ({
  userId: user?._id,
});

export default connect(mapStateToProps, null)(MyStartedThoughtsList);

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    alignSelf: "flex-start",
  },
  list: {
    position: "absolute",
    top: 0,
    left: SIDE_PADDING_LEFT,
    flexDirection: "row",
    backgroundColor: "transparent",
  },
  thot: {
    width: THOT_WIDTH,
    marginRight: THOT_SEPERATOR,
  },
  reload: {
    width: 30 + SIDE_PADDING_LEFT,
    justifyContent: "center",
    alignContent: "center",
    paddingLeft: SIDE_PADDING_LEFT,
  },
  reloadIndicator: {
    opacity: 0,
    ...(Platform.OS === "web"
      ? ({
          transitionProperty: "opacity",
          transitionDuration: "220ms",
          transitionTimingFunction: "ease-in-out",
        } as any)
      : {}),
  },
  reloadIndicatorVisible: {
    opacity: 1,
  },
  emptyWrap: {
    height: 0,
  },
});
